import type {
	PrefetchContext,
	PrefetchedDataProps,
	PrefetchedUserDataProps,
	PrefetchUserContext,
} from '#src/shared/prefetch';

import { useCountryCode, useLocale } from '#src/app/shared/hooks/l10n';
import type { MergedState } from '#src/app/shared/hooks/state';
import { useMergedStates } from '#src/app/shared/hooks/state';
import { useIsBot } from '#src/app/shared/hooks/userAgent';

import { prefetchHomepagePageDataFromApi, useHomepagePageDataFromApi } from './pageData';
import { prefetchHomepagePageUserDataFromApi, useHomepagePageUserDataFromApi } from './pageUserData';

export type HomepagePagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof useHomepagePageDataFromApi>[0];
}>;

export type HomepagePagePrefetchedUserContext = NonNullable<Parameters<typeof useHomepagePageUserDataFromApi>[0]>;

export type HomepagePageCombinedState = MergedState<
	ReturnType<typeof useHomepagePageDataFromApi>,
	ReturnType<typeof useHomepagePageUserDataFromApi>
>;

export function usePageContext(
	prefetchedData?: HomepagePagePrefetchedContext,
	prefetchedUserData?: HomepagePagePrefetchedUserContext,
): HomepagePageCombinedState {
	const locale = useLocale();
	const countryCode = useCountryCode();
	// the server-detected flag, so the first client render agrees with the SSR output
	const isBot = useIsBot();
	return useMergedStates(
		useHomepagePageDataFromApi(prefetchedData?.pageData, { locale, countryCode, isBot }),
		useHomepagePageUserDataFromApi(prefetchedUserData),
	);
}

export async function prefetchPageContext(context: PrefetchContext): Promise<HomepagePagePrefetchedContext> {
	const pageData = await prefetchHomepagePageDataFromApi(context, {
		locale: context.l10n.locale,
		countryCode: context.l10n.countryCode,
		isBot: context.bot,
	});

	return { pageData };
}

export async function prefetchPageUserContext(
	context: PrefetchUserContext,
): Promise<HomepagePagePrefetchedUserContext> {
	return prefetchHomepagePageUserDataFromApi(context);
}
export type HomepagePagePrefetchedDataProps = PrefetchedDataProps<HomepagePagePrefetchedContext> &
	PrefetchedUserDataProps<HomepagePagePrefetchedUserContext>;
